// Un champ de questionnaire, dessine une fois pour deux ecrans.
//
// Il vivait dans la page publique, et c'est la qu'il servait : l'apprenant
// repond, on dessine ses champs. Depuis que l'equipe peut **pre-remplir** un
// questionnaire avant de l'envoyer, le meme champ se dessine dans
// l'application — et deux renderers pour un meme champ finiraient par ne pas
// proposer la meme chose : un type ajoute d'un cote manquerait de l'autre, et
// le client verrait un formulaire qui ne ressemble pas a celui qu'on a rempli.
import React from 'react';

export default function QuestionField({ idx, question, value, onChange, primary = '#4F46E5' }) {
  const selectedStyle = { borderColor: primary, backgroundColor: `${primary}15` };
  const accentStyle = { accentColor: primary };
  const label = (
    <label className="block text-[13.5px] font-semibold text-[#1A1B2E] mb-2">
      {idx}. {question.label}
      {question.required && <span className="text-[#EF4444] ml-1">*</span>}
    </label>
  );

  if (question.type === 'text') {
    return (
      <div data-testid={`question-${question.id}`}>
        {label}
        <input
          type="text"
          value={value || ''}
          onChange={(e) => onChange(e.target.value)}
          className="w-full h-11 px-3.5 rounded-xl border border-[#EFF0F6] bg-white text-[13.5px] focus:outline-none transition-colors"
          style={{ borderColor: value ? primary : undefined }}
        />
      </div>
    );
  }

  if (question.type === 'long_text') {
    return (
      <div data-testid={`question-${question.id}`}>
        {label}
        <textarea
          rows={4}
          value={value || ''}
          onChange={(e) => onChange(e.target.value)}
          className="w-full px-3.5 py-2.5 rounded-xl border border-[#EFF0F6] bg-white text-[13.5px] focus:outline-none transition-colors resize-none"
          style={{ borderColor: value ? primary : undefined }}
        />
      </div>
    );
  }

  if (question.type === 'single') {
    return (
      <div data-testid={`question-${question.id}`}>
        {label}
        <div className="space-y-1.5">
          {(question.options || []).map((opt) => {
            const sel = value === opt;
            return (
              <label key={opt} className="flex items-center gap-2.5 px-3.5 py-2.5 rounded-xl border cursor-pointer transition-colors" style={sel ? selectedStyle : { borderColor: '#EFF0F6', backgroundColor: '#fff' }}>
                <input
                  type="radio"
                  name={question.id}
                  value={opt}
                  checked={sel}
                  onChange={() => onChange(opt)}
                  style={accentStyle}
                />
                <span className="text-[13.5px] text-[#1A1B2E]">{opt}</span>
              </label>
            );
          })}
        </div>
      </div>
    );
  }

  if (question.type === 'multi') {
    const arr = Array.isArray(value) ? value : [];
    const toggle = (opt) => {
      if (arr.includes(opt)) onChange(arr.filter((x) => x !== opt));
      else onChange([...arr, opt]);
    };
    return (
      <div data-testid={`question-${question.id}`}>
        {label}
        <div className="space-y-1.5">
          {(question.options || []).map((opt) => {
            const sel = arr.includes(opt);
            return (
              <label key={opt} className="flex items-center gap-2.5 px-3.5 py-2.5 rounded-xl border cursor-pointer transition-colors" style={sel ? selectedStyle : { borderColor: '#EFF0F6', backgroundColor: '#fff' }}>
                <input type="checkbox" checked={sel} onChange={() => toggle(opt)} style={accentStyle} />
                <span className="text-[13.5px] text-[#1A1B2E]">{opt}</span>
              </label>
            );
          })}
        </div>
      </div>
    );
  }

  if (question.type === 'scale') {
    const min = question.scale_min ?? 1;
    const max = question.scale_max ?? 5;
    const items = Array.from({ length: max - min + 1 }, (_, i) => min + i);
    return (
      <div data-testid={`question-${question.id}`}>
        {label}
        <div className="flex items-center gap-2 flex-wrap">
          {items.map((n) => {
            const sel = value === n;
            return (
              <button
                type="button"
                key={n}
                onClick={() => onChange(n)}
                style={sel ? { borderColor: primary, backgroundColor: primary, color: '#fff' } : undefined}
                className={`w-12 h-12 rounded-xl border text-[15px] font-bold transition-colors ${sel ? '' : 'border-[#EFF0F6] bg-white text-[#4A4B63] hover:border-[#C7B7FA]'}`}
              >
                {n}
              </button>
            );
          })}
        </div>
        <div className="flex justify-between text-[11px] text-[#8E8FA5] mt-1.5 px-1">
          <span>{min} — Faible</span>
          <span>{max} — Excellent</span>
        </div>
      </div>
    );
  }

  return null;
}
